// The operation note's sections (docs/plans/phase2-theatre.md §5.3). Signed as version 1;
// corrections are amendments, each a new version; nothing is edited in place.

import { err, ok, type Result } from '../result.ts';

/**
 * TODO(clinical-verify) T11: provisional headings until the clinical lead confirms them.
 * "Anaesthesia" is free text: Wodi structures and suggests no anaesthetic drug or dose.
 */
export const OPERATION_NOTE_SECTIONS = [
  { key: 'procedure', heading: 'Procedure performed' },
  { key: 'anaesthesia', heading: 'Anaesthesia' },
  { key: 'findings', heading: 'Findings' },
  { key: 'details', heading: 'Procedure details' },
  { key: 'complications', heading: 'Complications' },
  { key: 'blood_loss', heading: 'Estimated blood loss' },
  { key: 'specimens', heading: 'Specimens' },
  { key: 'post_op', heading: 'Post-operative instructions' },
] as const;

export type OperationNoteSectionKey = (typeof OPERATION_NOTE_SECTIONS)[number]['key'];

export interface OperationNoteSection {
  key: string;
  heading: string;
  text: string;
}

export const OPERATION_NOTE_MAX_SECTION = 20_000;

export type OperationNoteErrorCode =
  'operation_note.sections' | 'operation_note.procedure_required' | 'operation_note.too_long';

/**
 * Every section once, in the fixed order, text trimmed, headings from the list; the
 * procedure performed is written.
 */
export function checkOperationNote(
  sections: readonly { key: string; heading?: string; text: string }[],
): Result<OperationNoteSection[], OperationNoteErrorCode> {
  if (
    sections.length !== OPERATION_NOTE_SECTIONS.length ||
    sections.some((s, i) => s.key !== OPERATION_NOTE_SECTIONS[i]?.key)
  ) {
    return err('operation_note.sections');
  }
  const out = OPERATION_NOTE_SECTIONS.map((def, i) => ({
    key: def.key,
    heading: def.heading,
    text: (sections[i]?.text ?? '').trim(),
  }));
  if (out.some((s) => s.text.length > OPERATION_NOTE_MAX_SECTION)) {
    return err('operation_note.too_long');
  }
  if (out[0]?.text === '') return err('operation_note.procedure_required');
  return ok(out);
}
